import type { ReactNode } from 'react';
import {
  ArrowUp,
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudMoon,
  CloudRain,
  CloudSnow,
  CloudSun,
  Droplets,
  Gauge,
  Moon,
  RefreshCw,
  Sun,
  Thermometer,
  Umbrella,
  Wind,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { weatherInfo, type CurrentWeather, type WeatherIconKey } from '@/lib/weather';
import { compass, formatDD, type GeoPoint } from '@/lib/geo';
import { cn } from '@/lib/utils';

const ICONS: Record<WeatherIconKey, typeof Sun> = {
  sun: Sun,
  moon: Moon,
  'cloud-sun': CloudSun,
  'cloud-moon': CloudMoon,
  cloud: Cloud,
  fog: CloudFog,
  drizzle: CloudDrizzle,
  rain: CloudRain,
  snow: CloudSnow,
  storm: CloudLightning,
  thermometer: Thermometer,
};

interface WeatherPanelProps {
  location: GeoPoint;
  weather?: CurrentWeather;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Sun;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

export function WeatherPanel({ location, weather, isLoading, isError, refetch }: WeatherPanelProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="size-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !weather) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            Weather data is unavailable for this point right now.
          </p>
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="size-3.5" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const info = weatherInfo(weather.weatherCode, weather.isDay);
  const Icon = ICONS[info.icon];

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex size-14 items-center justify-center rounded-full bg-secondary">
              <Icon className="size-8 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold">
                  {weather.temperature.toFixed(1)}°C
                </span>
                <Badge variant={weather.isDay ? 'secondary' : 'outline'} className="gap-1">
                  {weather.isDay ? <Sun className="size-3" /> : <Moon className="size-3" />}
                  {weather.isDay ? 'Day' : 'Night'}
                </Badge>
              </div>
              <div className="truncate text-sm text-muted-foreground">{info.label}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-2">
        <Stat icon={Thermometer} label="Feels like" value={`${weather.apparent.toFixed(1)}°C`} />
        <Stat icon={Droplets} label="Humidity" value={`${weather.humidity.toFixed(0)} %`} />
        <Stat
          icon={Wind}
          label="Wind"
          value={
            <span className="inline-flex items-center gap-1">
              <ArrowUp
                className={cn('size-3.5 text-primary')}
                style={{ transform: `rotate(${(weather.windDirection + 180) % 360}deg)` }}
              />
              {weather.windSpeed.toFixed(1)} m/s from {compass(weather.windDirection)}
            </span>
          }
        />
        <Stat icon={Gauge} label="Pressure" value={`${weather.pressure.toFixed(0)} hPa`} />
        <Stat icon={Cloud} label="Cloud cover" value={`${weather.cloudCover.toFixed(0)} %`} />
        <Stat icon={Umbrella} label="Precipitation" value={`${weather.precipitation.toFixed(1)} mm`} />
      </div>

      <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
        Current conditions for <span className="font-mono">{formatDD(location)}</span> · timezone{' '}
        {weather.timezone} · data by{' '}
        <a
          href="https://open-meteo.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Open-Meteo
        </a>
        .
      </p>
    </div>
  );
}
